import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import type {
  EmpiricalStudyConsentChoice,
  EmpiricalStudyParticipantRecord,
  GameSession
} from '@/types';

const COLLECTION_NAME = 'empiricalStudyParticipants';

const createParticipantCode = (): string => {
  const timestamp = Date.now().toString(36).slice(-4).toUpperCase();
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `WSU-${timestamp}${random}`;
};

interface CreateParticipantRecordInput {
  session: GameSession;
  authUid: string;
  playerId: string;
  playerName: string;
  consentChoice: EmpiricalStudyConsentChoice;
  preSurveyResponses: Record<string, string>;
}

class EmpiricalStudyService {
  private mapRecord(
    data: Record<string, unknown>
  ): EmpiricalStudyParticipantRecord {
    const convertDate = (
      value: unknown
    ): Date | undefined => {
      if (!value) {
        return undefined;
      }

      if (value instanceof Date) {
        return value;
      }

      if (
        typeof value === 'object' &&
        value !== null &&
        'toDate' in value &&
        typeof (value as Timestamp).toDate === 'function'
      ) {
        return (value as Timestamp).toDate();
      }

      if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        if (Number.isFinite(parsed.getTime())) {
          return parsed;
        }
      }

      return undefined;
    };

    return {
      participantCode: String(data.participantCode || ''),
      sessionId: String(data.sessionId || ''),
      gameCode: String(data.gameCode || ''),
      teacherId: String(data.teacherId || ''),
      teacherName: String(data.teacherName || ''),
      questionSetId: String(data.questionSetId || ''),
      questionSetName: String(data.questionSetName || ''),
      empiricalStudyEnabled: Boolean(data.empiricalStudyEnabled),
      authUid: String(data.authUid || ''),
      playerId: String(data.playerId || ''),
      playerName: String(data.playerName || ''),
      consentChoice:
        data.consentChoice === 'consent' ? 'consent' : 'decline',
      preSurveyResponses:
        data.preSurveyResponses && typeof data.preSurveyResponses === 'object'
          ? (data.preSurveyResponses as Record<string, string>)
          : {},
      postSurveyResponses:
        data.postSurveyResponses && typeof data.postSurveyResponses === 'object'
          ? (data.postSurveyResponses as Record<string, string>)
          : {},
      status:
        typeof data.status === 'string'
          ? (data.status as EmpiricalStudyParticipantRecord['status'])
          : undefined,
      joinedAt: convertDate(data.joinedAt),
      preSurveySubmittedAt: convertDate(data.preSurveySubmittedAt),
      gameStartedAt: convertDate(data.gameStartedAt),
      gameCompletedAt: convertDate(data.gameCompletedAt),
      gameDurationSeconds:
        typeof data.gameDurationSeconds === 'number'
          ? data.gameDurationSeconds
          : undefined,
      finalScore:
        typeof data.finalScore === 'number' ? data.finalScore : undefined,
      postSurveySubmittedAt: convertDate(data.postSurveySubmittedAt),
      updatedAt: convertDate(data.updatedAt)
    };
  }

  async createParticipantRecord(
    input: CreateParticipantRecordInput
  ): Promise<string> {
    const participantCode = createParticipantCode();
    const docRef = doc(db, COLLECTION_NAME, participantCode);
    const hasConsented = input.consentChoice === 'consent';

    await setDoc(docRef, {
      participantCode,
      sessionId: input.session.id,
      gameCode: input.session.gameCode,
      teacherId: input.session.teacherId,
      teacherName: input.session.teacherName,
      questionSetId: input.session.questionSetId,
      questionSetName: input.session.questionSetName,
      empiricalStudyEnabled: Boolean(input.session.empiricalStudyEnabled),
      authUid: input.authUid,
      playerId: input.playerId,
      playerName: hasConsented ? input.playerName : '',
      consentChoice: input.consentChoice,
      preSurveyResponses: hasConsented ? input.preSurveyResponses : {},
      postSurveyResponses: {},
      status: hasConsented ? 'pre_survey_completed' : 'declined',
      joinedAt: serverTimestamp(),
      preSurveySubmittedAt: hasConsented ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    });

    return participantCode;
  }

  async markGameStarted(participantCode: string): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, participantCode);
    await updateDoc(docRef, {
      gameStartedAt: serverTimestamp(),
      status: 'in_game',
      updatedAt: serverTimestamp()
    });
  }

  async recordGameCompletion(
    participantCode: string,
    metrics: {
      finalScore: number;
      gameDurationSeconds?: number;
    }
  ): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, participantCode);
    await updateDoc(docRef, {
      finalScore: metrics.finalScore,
      gameDurationSeconds:
        typeof metrics.gameDurationSeconds === 'number'
          ? metrics.gameDurationSeconds
          : null,
      gameCompletedAt: serverTimestamp(),
      status: 'game_completed',
      updatedAt: serverTimestamp()
    });
  }

  async submitPostGameSurvey(
    participantCode: string,
    postSurveyResponses: Record<string, string>
  ): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, participantCode);
    await updateDoc(docRef, {
      postSurveyResponses,
      postSurveySubmittedAt: serverTimestamp(),
      status: 'post_survey_completed',
      updatedAt: serverTimestamp()
    });
  }

  async getParticipantRecordsForTeacher(
    teacherId: string
  ): Promise<EmpiricalStudyParticipantRecord[]> {
    const recordsQuery = query(
      collection(db, COLLECTION_NAME),
      where('teacherId', '==', teacherId)
    );

    const snapshot = await getDocs(recordsQuery);
    return snapshot.docs.map((recordDoc) =>
      this.mapRecord(recordDoc.data() as Record<string, unknown>)
    ).sort((a, b) => {
      const aTime = a.preSurveySubmittedAt?.getTime() ?? 0;
      const bTime = b.preSurveySubmittedAt?.getTime() ?? 0;
      return bTime - aTime;
    });
  }

  async getParticipantRecordsForSession(
    teacherId: string,
    sessionId: string
  ): Promise<EmpiricalStudyParticipantRecord[]> {
    const teacherRecords = await this.getParticipantRecordsForTeacher(teacherId);
    return teacherRecords
      .filter((record) => record.sessionId === sessionId)
      .sort((a, b) => {
        const aTime = a.preSurveySubmittedAt?.getTime() ?? 0;
        const bTime = b.preSurveySubmittedAt?.getTime() ?? 0;
        return aTime - bTime;
      });
  }
}

export const empiricalStudyService = new EmpiricalStudyService();
